import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Chip, Button, CardContent, Accordion, AccordionSummary, AccordionDetails, Collapse, Divider } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { API_URL } from '../../utils/auth';
import Auth from '../../utils/auth';
import { formatDateWithTime } from '../../utils/dateFormat';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';

const InteractionHistory = ({ clientId, reducedHeight = false }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [emailBodyMode, setEmailBodyMode] = useState('rendered');
  const [csmInfo, setCsmInfo] = useState(null);
  const workspaceId = Auth.getWorkspaceId();

  const withSafeLinks = (html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      Array.from(doc.querySelectorAll('a')).forEach((a) => {
        const href = (a.getAttribute('href') || '').trim();
        const lower = href.toLowerCase();
        const allowed =
          lower.startsWith('http://') ||
          lower.startsWith('https://') ||
          lower.startsWith('mailto:') ||
          lower.startsWith('tel:');
        if (!allowed) {
          a.removeAttribute('href');
          return;
        }
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      });
      return doc.body?.innerHTML || '';
    } catch {
      return html;
    }
  };

  const normalizeMetadata = (metadata) => {
    if (!metadata) return {};
    if (typeof metadata === 'object') return metadata;
    if (typeof metadata === 'string') {
      try {
        const parsed = JSON.parse(metadata);
        return parsed && typeof parsed === 'object' ? parsed : { body: metadata };
      } catch {
        return { body: metadata };
      }
    }
    return {};
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(d.getDate()).padStart(2, '0');
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const extractEmailLinks = (html) => {
    if (!html) return [];
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return Array.from(doc.querySelectorAll('a'))
        .map((a) => a.getAttribute('href'))
        .filter((href) => typeof href === 'string' && href.trim().length > 0);
    } catch {
      return [];
    }
  };

  const extractEmailPlainText = (html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const txt = doc.body?.textContent || '';
      return txt.replace(/\n{3,}/g, '\n\n').trim();
    } catch {
      return html;
    }
  };

  const fetchEvents = useCallback(async (resetOffset = false, currentOffsetValue = 0) => {
    if (!clientId || !workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const offsetToUse = resetOffset ? 0 : currentOffsetValue;

      const typeParam = filter !== 'all' ? filter : undefined;

      const response = await axios.get(`${API_URL}/clients/engagement-events`, {
        params: {
          clientId,
          workspaceId,
          type: typeParam,
          limit: 20,
          offset: offsetToUse
        }
      });

      const rawEvents = Array.isArray(response.data?.events) ? response.data.events : [];
      const newEvents = rawEvents.filter(event => {
        if (!event || typeof event !== 'object' || !event.id) return false;
        if (filter !== 'all' && event.type !== filter) {
          return false;
        }
        return true;
      });
      if (resetOffset) {
        setEvents(newEvents);
        setOffset(newEvents.length);
      } else {
        setEvents(prev => [...prev, ...newEvents]);
        setOffset(prev => prev + newEvents.length);
      }
      setHasMore(response.data?.hasMore === true);
    } catch (err) {
      console.error('Error fetching engagement events:', err);
      setError('Failed to load engagement events');
      setEvents([]);
      if (resetOffset) {
        setOffset(0);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId, workspaceId, filter]);

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    setEvents([]);
    setExpandedEventId(null);
    setEmailBodyMode('rendered');
    fetchEvents(true, 0);
  }, [fetchEvents]);

  useEffect(() => {
    const fetchCSMInfo = async () => {
      if (!clientId || !workspaceId) return;
      
      try {
        const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
          params: {
            clientId: clientId,
            workspaceId: workspaceId
          }
        });
        
        const clientData = response.data;
        if (clientData.csmName && clientData.csmName !== 'unassigned') {
          setCsmInfo({
            name: clientData.csmName
          });
        }
      } catch (err) {
        console.error('Error fetching CSM info:', err);
      }
    };
    
    fetchCSMInfo();
  }, [clientId, workspaceId]);

  const formatTimestamp = (dateString) => {
    if (!dateString) return 'Unknown date';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';

    const now = new Date();
    const diffMs = now - date;

    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMs < 0) return formatDateWithTime(dateString);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;

    return formatDateWithTime(dateString);
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'call':
        return <PhoneIcon sx={{ fontSize: 20, color: '#572AF1' }} />;
      case 'text':
        return <MessageIcon sx={{ fontSize: 20, color: '#572AF1' }} />;
      case 'email':
        return <EmailIcon sx={{ fontSize: 20, color: '#572AF1' }} />;
      case 'appointment':
        return <EventIcon sx={{ fontSize: 20, color: '#572AF1' }} />;
      default:
        return <MessageIcon sx={{ fontSize: 20, color: '#572AF1' }} />;
    }
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case 'call':
        return 'Call';
      case 'text':
        return 'Text';
      case 'email':
        return 'Email';
      case 'appointment':
        return 'Appointment';
      default:
        return type;
    }
  };

  const getEventDetails = (event) => {
    const { type, action } = event;
    const metadata = normalizeMetadata(event?.metadata);
    
    if (type === 'call') {
      const direction = metadata?.direction || action || 'unknown';
      const duration = metadata?.durationSec || 0;
      let durationStr = 'N/A';
      if (duration > 0) {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        if (minutes > 0) {
          durationStr = `${minutes} min${minutes > 1 ? 's' : ''}${seconds > 0 ? ` ${seconds} sec${seconds > 1 ? 's' : ''}` : ''}`;
        } else {
          durationStr = `${seconds} sec${seconds > 1 ? 's' : ''}`;
        }
      }
      return {
        title: `${direction === 'inbound' ? 'Incoming' : 'Outgoing'} Call`,
        subtitle: `Duration: ${durationStr}`,
        phone: metadata?.from || metadata?.to || 'Unknown'
      };
    }
    
    if (type === 'text') {
      const channel = metadata?.channel || 'sms';
      const direction = metadata?.direction || action || 'unknown';
      const channelLabel = channel.charAt(0).toUpperCase() + channel.slice(1);
      const body = metadata?.body || '';
      const preview = body.length > 50 ? body.substring(0, 50) + '...' : body;
      return {
        title: `${direction === 'inbound' ? 'Received' : 'Sent'} ${channelLabel}`,
        subtitle: preview || 'No content',
        phone: metadata?.from || metadata?.to || 'Unknown'
      };
    }
    
    if (type === 'email') {
      const direction = metadata?.direction || action || 'unknown';
      const subject = metadata?.subject || 'No subject';
      const body = metadata?.body || '';
      const preview = body.length > 50 ? body.substring(0, 50) + '...' : body;
      const to = metadata?.to;
      const from = metadata?.from || 'Unknown';
      const recipient = Array.isArray(to) ? to.join(', ') : (to || from);
      return {
        title: `${direction === 'inbound' ? 'Received' : 'Sent'} Email`,
        subtitle: subject || preview || 'No content',
        phone: recipient
      };
    }
    
    if (type === 'appointment') {
      const title = metadata?.title || 'Appointment';
      const status = metadata?.status || action || 'scheduled';
      return {
        title: title,
        subtitle: `Status: ${status}`,
        phone: null
      };
    }
    
    return {
      title: getEventTypeLabel(type),
      subtitle: '',
      phone: null
    };
  };

  const handleLoadMore = () => {
    fetchEvents(false, offset);
  };

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const handleToggleEvent = (eventId) => {
    setExpandedEventId((prev) => (prev === eventId ? null : eventId));
    setEmailBodyMode('rendered');
  };

  const DetailRow = ({ label, value, stacked = false }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: stacked ? 'column' : 'row',
          gap: stacked ? '4px' : '10px',
          padding: '4px 0',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#666',
            minWidth: stacked ? undefined : '90px',
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#333', wordBreak: 'break-word' }}>
          {value}
        </Typography>
      </Box>
    );
  };

  const renderExpandedDetails = (event) => {
    const meta = normalizeMetadata(event?.metadata);
    const type = event?.type;

    if (type === 'email') {
      const subject = meta.subject || null;
      const direction = meta.direction || event?.action || null;
      const from = meta.from || null;
      const to = Array.isArray(meta.to) ? meta.to.join(', ') : (meta.to || null);
      const rawBody = typeof meta.body === 'string' ? meta.body : '';
      const sanitized = withSafeLinks(DOMPurify.sanitize(rawBody, { USE_PROFILES: { html: true } }));

      return (
        <Box sx={{ paddingTop: '10px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
            <DetailRow label="Subject" value={subject} stacked />
            <DetailRow label="Direction" value={direction} stacked />
            <DetailRow label="From" value={from} stacked />
            <DetailRow label="To" value={to} stacked />
          </Box>
          {rawBody ? (
            <Box sx={{ marginTop: '10px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}>Body</Typography>
                <Button
                  size="small"
                  onClick={() => setEmailBodyMode((m) => (m === 'rendered' ? 'raw' : 'rendered'))}
                  sx={{
                    backgroundColor: 'rgba(87, 42, 241, 0.10)',
                    color: '#572AF1',
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    padding: '4px 10px',
                    '&:hover': { backgroundColor: 'rgba(87, 42, 241, 0.16)' },
                  }}
                >
                  {emailBodyMode === 'rendered' ? 'View raw' : 'View rendered'}
                </Button>
              </Box>
              <Box
                sx={{
                  borderRadius: '10px',
                  backgroundColor: 'rgba(246, 244, 255, 0.5)',
                  padding: '10px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                {emailBodyMode === 'rendered' ? (
                  <Box sx={{ fontSize: '0.85rem', color: '#333', '& a': { color: '#572AF1' } }} dangerouslySetInnerHTML={{ __html: sanitized }} />
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {rawBody}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    }

    if (type === 'text') {
      const direction = meta.direction || event?.action || null;
      const from = meta.from || null;
      const to = meta.to || null;
      const channel = meta.channel || null;
      const body = typeof meta.body === 'string' ? meta.body : '';

      return (
        <Box sx={{ paddingTop: '10px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
            <DetailRow label="Direction" value={direction} stacked />
            <DetailRow label="Channel" value={channel} stacked />
            <DetailRow label="From" value={from} stacked />
            <DetailRow label="To" value={to} stacked />
          </Box>
          {body ? (
            <Box sx={{ marginTop: '10px' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Message</Typography>
              <Box
                sx={{
                  borderRadius: '10px',
                  backgroundColor: 'rgba(246, 244, 255, 0.5)',
                  padding: '10px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {body}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    }

    if (type === 'appointment') {
      const title = meta.title || null;
      const status = meta.status || event?.action || null;
      const startTime = formatDateTime(meta.startTime);
      const endTime = formatDateTime(meta.endTime);
      const calendarId = meta.calendarId || null;
      const locationId = meta.locationId || null;
      const userId = meta.userId || null;

      return (
        <Box sx={{ paddingTop: '10px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
            <DetailRow label="Title" value={title} stacked />
            <DetailRow label="Status" value={status} stacked />
            <DetailRow label="Start" value={startTime} stacked />
            <DetailRow label="End" value={endTime} stacked />
            <DetailRow label="Calendar" value={calendarId} stacked />
            <DetailRow label="Location" value={locationId} stacked />
            <DetailRow label="User ID" value={userId} stacked />
          </Box>
        </Box>
      );
    }

    if (type === 'call') {
      const direction = meta.direction || event?.action || null;
      const from = meta.from || null;
      const to = meta.to || null;
      const durationSec = meta.durationSec;
      const durationStr = typeof durationSec === 'number' && durationSec > 0 ? `${durationSec} sec` : null;
      const metaString = meta && Object.keys(meta).length ? JSON.stringify(meta, null, 2) : '';

      return (
        <Box sx={{ paddingTop: '10px' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
            <DetailRow label="Direction" value={direction} stacked />
            <DetailRow label="Duration" value={durationStr} stacked />
            <DetailRow label="From" value={from} stacked />
            <DetailRow label="To" value={to} stacked />
          </Box>
          {metaString ? (
            <Box sx={{ marginTop: '10px' }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Other</Typography>
              <Box
                sx={{
                  borderRadius: '10px',
                  backgroundColor: 'rgba(246, 244, 255, 0.5)',
                  padding: '10px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {metaString}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      );
    }

    const metaString = event?.metadata ? (typeof event.metadata === 'string' ? event.metadata : JSON.stringify(event.metadata, null, 2)) : '';
    return (
      <Box sx={{ paddingTop: '10px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '12px' }}>
          <DetailRow label="Type" value={event?.type || null} stacked />
          <DetailRow label="Action" value={event?.action || null} stacked />
          <DetailRow label="Event ID" value={event?.id || null} stacked />
        </Box>
        {metaString ? (
          <Box sx={{ marginTop: '10px' }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Metadata</Typography>
            <Box
              sx={{
                borderRadius: '10px',
                backgroundColor: 'rgba(246, 244, 255, 0.5)',
                padding: '10px',
                maxHeight: '240px',
                overflowY: 'auto',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {metaString}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 800,
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'height 0.3s ease',
        marginTop: '10px',
        marginBottom: '0%',
        background: 'rgba(255, 255, 255, 1)',
        boxSizing: 'border-box',
      }}
    >
      <CardContent sx={{ padding: '0 !important', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0,
            },
            background: 'transparent',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              padding: '15px',
              '& .MuiAccordionSummary-content': {
                margin: 0,
                alignItems: 'center',
                flexDirection: 'column',
                gap: '4px',
              },
      }}
    >
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            sx={{
              fontSize: '1.4rem',
              color: 'black',
              fontWeight: 'bold',
              margin: 0,
            }}
          >
            Interaction History
          </Typography>
          {csmInfo && csmInfo.name && (
            <Chip
              label={`CSM: ${csmInfo.name}`}
              size="small"
              sx={{
                backgroundColor: 'rgba(87, 42, 241, 0.1)',
                color: '#572AF1',
                fontSize: '0.7rem',
                height: '24px',
                fontWeight: 'normal',
              }}
            />
          )}
        </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              padding: '0 15px 15px 15px',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: reducedHeight ? '320px' : '400px',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
                {['all', 'call', 'text', 'email', 'appointment'].map((filterType) => (
                  <Chip
                    key={filterType}
                    label={filterType === 'all' ? 'All' : getEventTypeLabel(filterType)}
                    onClick={() => setFilter(filterType)}
                    sx={{
                      backgroundColor: filter === filterType ? '#572AF1' : 'rgba(246, 244, 255, 1)',
                      color: filter === filterType ? '#FFFFFF !important' : '#572AF1',
                      fontWeight: filter === filterType ? 'bold' : 'normal',
                      fontSize: '0.75rem',
                      height: '28px',
                      '&:hover': {
                        backgroundColor: filter === filterType ? '#4A21D1' : 'rgba(246, 244, 255, 0.8)',
                      },
                      '& .MuiChip-label': {
                        color: filter === filterType ? '#FFFFFF !important' : '#572AF1',
                      },
                    }}
                  />
                ))}
              </Box>

      <Box
        sx={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '5px',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'rgba(246, 244, 255, 0.5)',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#572AF1',
                    borderRadius: '10px',
                  },
                }}
              >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress size={30} sx={{ color: '#572AF1' }} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column', gap: '10px' }}>
            <Typography sx={{ color: '#d32f2f', fontSize: '0.9rem' }}>{error}</Typography>
            <Button
              size="small"
              onClick={() => fetchEvents(true, 0)}
              sx={{
                backgroundColor: '#572AF1',
                color: 'white',
                '&:hover': { backgroundColor: '#4A21D1' },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', flexDirection: 'column', gap: '10px' }}>
            <Typography sx={{ color: '#666', fontSize: '0.9rem', textAlign: 'center' }}>
              No engagement events found
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '0.75rem', textAlign: 'center' }}>
              Events will appear here once synced from HighLevel
            </Typography>
          </Box>
        ) : (
          <>
            {events.map((event, index) => {
              const details = getEventDetails(event);
              const eventKey = event.id || `event-${index}`;
              const isRowExpanded = expandedEventId === eventKey;
              return (
                <Box
                  key={eventKey}
                  sx={{
          display: 'flex',
                    flexDirection: 'column',
                    padding: '12px',
                    marginBottom: '10px',
                    backgroundColor: 'rgba(246, 244, 255, 0.5)',
                    borderRadius: '10px',
                    borderLeft: '3px solid #572AF1',
                    '&:hover': {
                      backgroundColor: 'rgba(246, 244, 255, 0.8)',
                    },
                  }}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => handleToggleEvent(eventKey)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleToggleEvent(eventKey);
                    }}
                    sx={{ display: 'flex', gap: '12px', cursor: 'pointer' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', marginTop: '2px' }}>
                      {getEventIcon(event.type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 'normal',
                          color: '#333',
                          lineHeight: 1.2,
                        }}
                      >
                        {details.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#999',
                            marginLeft: '8px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatTimestamp(event?.occurredAt)}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            transform: isRowExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.15s ease',
                            color: '#572AF1',
                          }}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </Box>
                      </Box>
                    </Box>
                    {details.subtitle && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#666',
                          marginBottom: '4px',
                          lineHeight: 1.3,
                        }}
                      >
                        {details.subtitle}
                      </Typography>
                    )}
                    {details.phone && (
        <Typography
          sx={{
                          fontSize: '0.7rem',
                          color: '#999',
          }}
        >
                        {details.phone}
        </Typography>
                    )}
                  </Box>
                  </Box>
                  <Collapse in={isRowExpanded} timeout={180} unmountOnExit>
                    <Divider sx={{ marginTop: '10px', marginBottom: '10px' }} />
                    {renderExpandedDetails(event)}
                  </Collapse>
                </Box>
              );
            })}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <Button
                  size="small"
                  onClick={handleLoadMore}
                  disabled={loading}
                  sx={{
                    backgroundColor: '#572AF1',
                    color: 'white',
                    fontSize: '0.75rem',
                    padding: '6px 16px',
                    '&:hover': { backgroundColor: '#4A21D1' },
                    '&:disabled': { backgroundColor: '#ccc' },
                  }}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
              </Box>
    </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Box>
  );
};

export default InteractionHistory;
